import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setToken } from '../utils/auth';
import CustomerAgreement from '../components/CustomerAgreement';

type Step = 'registration' | 'agreement';

interface RegistrationResponse {
  token: string;
  userId: string;
  clientId: string;
}

const MarketplaceRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useAuth();

  const registrationToken = searchParams.get('registration_token');
  const errorParam = searchParams.get('error');

  const [step, setStep] = useState<Step>('registration');
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (errorParam) {
    const errorMessages: Record<string, string> = {
      missing_token: 'No subscription token was provided. Please return to AWS Marketplace and try again.',
      resolve_failed: 'We could not verify your AWS Marketplace subscription. Please try again or contact support.',
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Subscription Error
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {errorMessages[errorParam] || 'An unexpected error occurred. Please try again.'}
          </p>
          <a
            href="mailto:support@aivilo.ai"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Contact Support
          </a>
        </div>
      </div>
    );
  }

  if (!registrationToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Invalid Registration Link
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This page can only be accessed from AWS Marketplace. Please subscribe through AWS Marketplace to get started.
          </p>
        </div>
      </div>
    );
  }

  const handleRegistrationSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setStep('agreement');
    setHasScrolledToBottom(false);
  };

  const handleCompleteRegistration = async () => {
    setLoading(true);
    setError('');

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.dev.aivilo.ai/core/api/v1';

      const response = await fetch(`${baseUrl}/marketplace/complete-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registration_token: registrationToken,
          email,
          name,
          company,
          password,
          agreed_to_terms: true,
        }),
      });

      const data = await response.json() as RegistrationResponse & { detail?: string };

      if (!response.ok) {
        setError(data.detail || 'Registration failed. Please try again.');
        setStep('registration');
        return;
      }

      setToken(data.token);
      await refreshAuth();
      navigate('/connect-aws');
    } catch {
      setError('Network error. Please try again.');
      setStep('registration');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'agreement') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-white">
              Aivilo Lens Customer Agreement
            </h1>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Effective Date: Upon Customer&apos;s acceptance
            </p>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Version: 1.4 | Last Updated: January 29, 2026
            </p>
          </div>

          <CustomerAgreement onScrolledToBottom={() => setHasScrolledToBottom(true)} />

          <div className="flex flex-col items-center space-y-4">
            {!hasScrolledToBottom && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Please read through the agreement to continue
              </p>
            )}

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4 max-w-md">
                <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
              </div>
            )}

            <button
              onClick={handleCompleteRegistration}
              disabled={!hasScrolledToBottom || loading}
              className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating Account...' : 'I Agree & Complete Registration'}
            </button>

            <button
              onClick={() => setStep('registration')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
            >
              Back to registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-auto flex justify-center">
            <img src="/lens-logo-dark.png" alt="Aivilo Lens" className="h-12" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Complete Your Registration
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Your AWS Marketplace subscription is confirmed. Set up your account to get started.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleRegistrationSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company Name
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                value={company}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompany(e.target.value)}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your company name"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="At least 8 characters"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
            </div>
          )}

          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Continue to Agreement
          </button>
        </form>
      </div>
    </div>
  );
};

export default MarketplaceRegisterPage;
